import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import { ChartContainer, CHART_COLORS, useAdminContext } from './shared';
import './AdminPages.css';

interface ResponseData {
  name: string;
  totalUsers: number;
  completedUsers: number;
  completionRate: number;
}

interface DrilldownData {
  parent: {
    name: string;
    groupBy: string;
    totalUsers: number;
    completedUsers: number;
    completionRate: number;
  };
  children: ResponseData[];
}

type GroupBy = 'survey_type' | 'school' | 'grade';

const ResponseRates: React.FC = () => {
  const { token } = useAuth();
  const { respondentType } = useAdminContext();
  const [groupBy, setGroupBy] = useState<GroupBy>('survey_type');
  const [data, setData] = useState<ResponseData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Drill-down state
  const [drilldownParent, setDrilldownParent] = useState<string | null>(null);
  const [drilldownData, setDrilldownData] = useState<DrilldownData | null>(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;

      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`/api/admin/stats/responses?groupBy=${groupBy}&role=${respondentType}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const result = await response.json();
        setData(result.data);
      } catch (err: any) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
    // Reset drilldown when groupBy or respondentType changes
    setDrilldownParent(null);
    setDrilldownData(null);
  }, [token, groupBy, respondentType]);

  // Fetch drilldown data
  useEffect(() => {
    const fetchDrilldown = async () => {
      if (!token || !drilldownParent) return;

      setDrilldownLoading(true);

      try {
        const response = await fetch(
          `/api/admin/stats/drilldown?groupBy=${groupBy}&parent=${encodeURIComponent(drilldownParent)}&role=${respondentType}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const result = await response.json();
          setDrilldownData(result);
        }
      } catch (err) {
        console.error('Failed to fetch drilldown:', err);
      } finally {
        setDrilldownLoading(false);
      }
    };

    fetchDrilldown();
  }, [token, drilldownParent, groupBy, respondentType]);

  const handleBarClick = (data: any) => {
    if (groupBy === 'school' || groupBy === 'grade') {
      setDrilldownParent(data.name);
    }
  };

  const closeDrilldown = () => {
    setDrilldownParent(null);
    setDrilldownData(null);
  };

  const getBarColor = (rate: number) => {
    if (rate >= 75) return CHART_COLORS.success;
    if (rate >= 50) return CHART_COLORS.primary;
    if (rate >= 25) return CHART_COLORS.warning;
    return CHART_COLORS.danger;
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      return (
        <div className="rr-tooltip">
          <p className="rr-tooltip-name">{item.name}</p>
          <p className="rr-tooltip-rate">{item.completionRate}% completion</p>
          <p className="rr-tooltip-detail">
            {item.completedUsers} of {item.totalUsers} users
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="admin-page response-rates-page">
      <div className="page-header">
        <div>
          <h2>Response Rates</h2>
          <p className="page-subtitle">Survey completion rates across the district</p>
        </div>
        <div className="filter-controls">
          <label htmlFor="groupBy">Group by:</label>
          <select
            id="groupBy"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
          >
            <option value="survey_type">Survey Type</option>
            <option value="school">School</option>
            {respondentType === 'student' && (
              <option value="grade">Grade Level</option>
            )}
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading data...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>Error: {error}</p>
        </div>
      ) : (
        <>
          {/* Chart View */}
          <ChartContainer
            title={`Completion by ${groupBy === 'survey_type' ? 'Survey Type' : groupBy === 'school' ? 'School' : 'Grade Level'}`}
            subtitle={groupBy !== 'survey_type' ? 'Click a bar to drill down' : undefined}
            className="rr-chart"
          >
            <ResponsiveContainer width="100%" height={Math.max(300, data.length * 45)}>
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 120, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                <XAxis
                  type="number"
                  domain={[0, 100]}
                  tickFormatter={(v) => `${v}%`}
                  tick={{ fontSize: 12, fill: '#6d6d70' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#1d1d1f' }}
                  width={115}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="completionRate"
                  radius={[0, 4, 4, 0]}
                  cursor={groupBy !== 'survey_type' ? 'pointer' : 'default'}
                  onClick={(data) => handleBarClick(data)}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.completionRate)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartContainer>

          {/* Table View */}
          <div className="rr-table-section">
            <h3>Detailed Breakdown</h3>
            <div className="data-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Total Users</th>
                    <th>Completed</th>
                    <th>Completion Rate</th>
                    {groupBy !== 'survey_type' && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.map((item, index) => (
                    <tr key={index}>
                      <td className="rr-cell-name">{item.name}</td>
                      <td>{item.totalUsers.toLocaleString()}</td>
                      <td>{item.completedUsers.toLocaleString()}</td>
                      <td>
                        <div className="progress-cell">
                          <div
                            className="progress-bar"
                            style={{
                              width: `${item.completionRate}%`,
                              background: getBarColor(item.completionRate),
                            }}
                          />
                          <span>{item.completionRate}%</span>
                        </div>
                      </td>
                      {groupBy !== 'survey_type' && (
                        <td>
                          <button
                            className="rr-drilldown-btn"
                            onClick={() => setDrilldownParent(item.name)}
                          >
                            View Details
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={groupBy !== 'survey_type' ? 5 : 4} className="empty-state">
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Drilldown Modal */}
          {drilldownParent && (
            <div className="rr-modal-overlay" onClick={closeDrilldown}>
              <div className="rr-modal" onClick={(e) => e.stopPropagation()}>
                <div className="rr-modal-header">
                  <div>
                    <h3>{drilldownParent}</h3>
                    {drilldownData && (
                      <p className="rr-modal-subtitle">
                        {drilldownData.parent.completionRate}% overall completion
                        ({drilldownData.parent.completedUsers} of {drilldownData.parent.totalUsers})
                      </p>
                    )}
                  </div>
                  <button className="rr-modal-close" onClick={closeDrilldown}>
                    &times;
                  </button>
                </div>
                <div className="rr-modal-body">
                  {drilldownLoading ? (
                    <div className="loading-state">
                      <div className="loading-spinner" />
                      <p>Loading...</p>
                    </div>
                  ) : drilldownData && drilldownData.children.length > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={Math.max(200, drilldownData.children.length * 40)}>
                        <BarChart
                          data={drilldownData.children}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                          <XAxis
                            type="number"
                            domain={[0, 100]}
                            tickFormatter={(v) => `${v}%`}
                            tick={{ fontSize: 12, fill: '#6d6d70' }}
                          />
                          <YAxis
                            type="category"
                            dataKey="name"
                            tick={{ fontSize: 12, fill: '#1d1d1f' }}
                            width={95}
                          />
                          <Tooltip content={<CustomTooltip />} />
                          <Bar dataKey="completionRate" radius={[0, 4, 4, 0]}>
                            {drilldownData.children.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={getBarColor(entry.completionRate)} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      <div className="rr-drilldown-table">
                        <table className="data-table">
                          <thead>
                            <tr>
                              <th>{groupBy === 'school' ? 'Grade' : 'School'}</th>
                              <th>Users</th>
                              <th>Completed</th>
                              <th>Rate</th>
                            </tr>
                          </thead>
                          <tbody>
                            {drilldownData.children.map((item, index) => (
                              <tr key={index}>
                                <td>{item.name}</td>
                                <td>{item.totalUsers}</td>
                                <td>{item.completedUsers}</td>
                                <td>{item.completionRate}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  ) : (
                    <div className="empty-state">No breakdown data available</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ResponseRates;
